import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals } from '../shared';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3 } from 'lucide-react';

const RefundManagement = (() => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('search'); // search, requests, history
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Selected operation for refund
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [refundCalculation, setRefundCalculation] = useState(null);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  
  // Refund form states
  const [refundType, setRefundType] = useState('full_refund');
  const [customRefundAmount, setCustomRefundAmount] = useState('');
  const [manualFees, setManualFees] = useState(''); // NEW: Manual fees input
  const [refundReason, setRefundReason] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  
  // Refunds list
  const [refundsList, setRefundsList] = useState([]);
  const [refundsLoading, setRefundsLoading] = useState(false);

  // Search operations
  const searchOperations = async () => {
    if (!searchQuery.trim()) {
      alert('يرجى إدخال اسم العميل أو رقم العملية');
      return;
    }

    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/refunds/search-operations?query=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSearchResults(response.data);
      if (response.data.length === 0) {
        alert('لم يتم العثور على عمليات تطابق البحث');
      }
    } catch (error) {
      console.error('Error searching operations:', error);
      alert('فشل في البحث: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSearching(false);
    }
  };

  // Calculate refund for selected operation
  const calculateRefund = async (operation) => {
    if (operation.has_refund) {
      alert('هذه العملية لديها طلب استرجاع مسبق');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/refunds/calculate`, {
        original_operation_id: operation.id,
        refund_type: refundType,
        custom_amount: refundType === 'partial_refund' ? parseFloat(customRefundAmount) || null : null,
        manual_fees: parseFloat(manualFees) || 0.0  // Manual fees input
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedOperation(operation);
      setRefundCalculation(response.data);
      setShowRefundDialog(true);
    } catch (error) {
      console.error('Error calculating refund:', error);
      alert('فشل في حساب الاسترجاع: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Create refund request
  const createRefund = async () => {
    if (!refundReason.trim()) {
      alert('يرجى إدخال سبب الاسترجاع');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/refunds`, {
        original_operation_id: selectedOperation.id,
        refund_type: refundType,
        reason: refundReason,
        notes: refundNotes,
        custom_refund_amount: refundType === 'partial_refund' ? parseFloat(customRefundAmount) || null : null,
        manual_fees: parseFloat(manualFees) || 0.0  // Manual fees input
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم إنشاء طلب الاسترجاع بنجاح');
      setShowRefundDialog(false);
      setSelectedOperation(null);
      setRefundCalculation(null);
      setRefundReason('');
      setRefundNotes('');
      setCustomRefundAmount('');
      
      // Refresh search results
      searchOperations();
      fetchRefunds();
    } catch (error) {
      console.error('Error creating refund:', error);
      alert('فشل في إنشاء طلب الاسترجاع: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch refunds list
  const fetchRefunds = async () => {
    setRefundsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/refunds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRefundsList(response.data);
    } catch (error) {
      console.error('Error fetching refunds:', error);
      alert('فشل في تحميل قائمة الاسترجاع');
    } finally {
      setRefundsLoading(false);
    }
  };

  // Approve refund
  const approveRefund = async (refundId) => {
    if (!confirm('هل أنت متأكد من الموافقة على الاسترجاع؟ سيتم خصم المبلغ من رصيد الوكالة المعنية.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/refunds/${refundId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم الموافقة على الاسترجاع وخصم المبلغ من رصيد الوكالة');
      fetchRefunds();
    } catch (error) {
      console.error('Error approving refund:', error);
      alert('❌ فشل في الموافقة: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Reject refund
  const rejectRefund = async (refundId) => {
    const reason = prompt('سبب الرفض (اختياري):');
    if (reason === null) return; // User cancelled

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/refunds/${refundId}/reject?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم رفض طلب الاسترجاع');
      fetchRefunds();
    } catch (error) {
      console.error('Error rejecting refund:', error);
      alert('❌ فشل في الرفض: ' + (error.response?.data?.detail || error.message));
    }
  };

  useEffect(() => {
    if (activeTab === 'requests' || activeTab === 'history') {
      fetchRefunds();
    }
  }, [activeTab]);

  // Auto-fill from a Daily Operation that has a pending refund credit
  // (set in sessionStorage when user clicks the ↩️ button in Daily Ops)
  useEffect(() => {
    const raw = typeof window !== 'undefined' ? sessionStorage.getItem('refund_pending_operation') : null;
    if (!raw) return;
    try {
      const ctx = JSON.parse(raw);
      // Search the operation, then auto-select it for refund processing
      (async () => {
        try {
          const r = await axios.get(
            `${API}/refunds/search-operations?query=${encodeURIComponent(ctx.operation_no || ctx.client_name || '')}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          const ops = r.data || [];
          const match = ops.find(o => o.operation_no === ctx.operation_no || o.id === ctx.operation_id) || ops[0];
          if (match) {
            setSelectedOperation(match);
            setRefundType('partial_refund');
            setCustomRefundAmount(String(ctx.credit_amount || ''));
            setRefundReason(`تخفيض سعر الخدمة (${ctx.service_name || ''})`);
            setActiveTab('search');
            // Optionally auto-calculate
            setTimeout(() => calculateRefund(match), 300);
          }
        } catch (e) {
          console.warn('refund pre-fill error', e);
        } finally {
          sessionStorage.removeItem('refund_pending_operation');
        }
      })();
    } catch (e) {
      sessionStorage.removeItem('refund_pending_operation');
    }
  }, []);

  if (!user) {
    return <div className="p-4 text-center text-red-600">يرجى تسجيل الدخول</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">↩️ إدارة الاسترجاع</h1>
          <p className="text-sm text-gray-600 mt-1">استرجاع الأموال للعملاء مع حساب الرسوم</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'search', label: '🔍 بحث العمليات', icon: '🔍' },
            { id: 'requests', label: '📋 طلبات الاسترجاع', icon: '📋' },
            { id: 'history', label: '📊 سجل الاسترجاع', icon: '📊' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="space-y-6">
          {/* Search Form */}
          <Card>
            <CardHeader>
              <CardTitle>🔍 البحث عن العمليات للاسترجاع</CardTitle>
              <CardDescription>
                ابحث بالاسم أو رقم العملية للعمليات المؤكدة
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-4 rtl:space-x-reverse">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="اسم العميل أو رقم العملية"
                  className="flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && searchOperations()}
                />
                <Button 
                  onClick={searchOperations}
                  disabled={searching || !searchQuery.trim()}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  {searching ? 'جاري البحث...' : '🔍 بحث'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>📋 نتائج البحث</CardTitle>
                <CardDescription>
                  العمليات المطابقة للبحث ({searchResults.length} عملية)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {searchResults.map((operation) => (
                    <div key={operation.id} className="p-4 border rounded-lg bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex flex-col space-y-1">
                          <span className="font-bold text-lg text-gray-800">
                            {operation.operation_no}
                          </span>
                          <span className="text-sm text-gray-600">
                            🎫 {operation.service_name}
                          </span>
                          <span className="text-sm text-brand-600">
                            👤 {operation.client_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            📅 {new Date(operation.date).toLocaleDateString('ar-DZ', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        <div className="text-right space-y-2">
                          <div className="text-xl font-bold text-green-600">
                            {operation.final_price?.toLocaleString()} دج
                          </div>
                          {operation.has_refund ? (
                            <div className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                              {operation.refund_status === 'pending' && '⏳ استرجاع معلق'}
                              {operation.refund_status === 'approved' && '✅ تم الاسترجاع'}
                              {operation.refund_status === 'rejected' && '❌ مرفوض'}
                              {operation.refund_status === 'paid' && '💰 تم الدفع'}
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedOperation(operation);
                                setShowRefundDialog(true);
                              }}
                              className="bg-orange-600 hover:bg-orange-700"
                              disabled={loading}
                            >
                              ↩️ طلب استرجاع
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Refund Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>📋 طلبات الاسترجاع المعلقة</CardTitle>
              <CardDescription>
                طلبات الاسترجاع التي تحتاج موافقة
              </CardDescription>
            </CardHeader>
            <CardContent>
              {refundsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {refundsList.filter(r => r.status === 'pending').length === 0 ? (
                    <p className="text-center text-gray-500 py-4">لا توجد طلبات استرجاع معلقة</p>
                  ) : (
                    refundsList.filter(r => r.status === 'pending').map((refund) => (
                      <div key={refund.id} className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex flex-col space-y-1">
                            <span className="font-bold text-orange-800">
                              {refund.original_operation_no}
                            </span>
                            <span className="text-sm text-orange-700">
                              👤 {refund.client_name}
                            </span>
                            <span className="text-sm text-gray-600">
                              🎫 {refund.service_name}
                            </span>
                            <span className="text-xs text-gray-500">
                              💭 {refund.reason}
                            </span>
                            {refund.notes && (
                              <span className="text-xs text-gray-400">
                                📝 {refund.notes}
                              </span>
                            )}
                          </div>
                          <div className="text-right space-y-2">
                            <div className="text-lg font-bold text-gray-700">
                              المبلغ الأصلي: {refund.original_amount?.toLocaleString()} دج
                            </div>
                            <div className="text-sm text-red-600">
                              رسوم ({refund.fee_percentage}%): -{refund.fees_amount?.toLocaleString()} دج
                            </div>
                            <div className="text-lg font-bold text-green-600">
                              صافي الاسترجاع: {refund.net_refund_amount?.toLocaleString()} دج
                            </div>
                            
                            {(user?.role === 'general_accountant' || user?.role === 'super_admin') && (
                              <div className="flex space-x-2 mt-2">
                                <Button
                                  size="sm"
                                  onClick={() => approveRefund(refund.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                >
                                  ✅ موافقة ودفع
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => rejectRefund(refund.id)}
                                  className="text-red-600 hover:text-red-700 border-red-300 text-xs"
                                >
                                  ❌ رفض
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-sm text-orange-700">
                          <span>طلب من: {refund.requested_by_name}</span>
                          <span>
                            {new Date(refund.requested_at).toLocaleDateString('ar-DZ', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>📊 سجل عمليات الاسترجاع</CardTitle>
              <CardDescription>
                جميع عمليات الاسترجاع (مدفوعة ومرفوضة)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {refundsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {refundsList.filter(r => r.status !== 'pending').length === 0 ? (
                    <p className="text-center text-gray-500 py-4">لا توجد عمليات استرجاع مكتملة</p>
                  ) : (
                    refundsList.filter(r => r.status !== 'pending').map((refund) => (
                      <div key={refund.id} className={`p-4 border rounded-lg ${
                        refund.status === 'paid' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex flex-col space-y-1">
                            <span className="font-bold text-gray-800">
                              {refund.original_operation_no}
                            </span>
                            <span className="text-sm text-gray-600">
                              👤 {refund.client_name} | 🎫 {refund.service_name}
                            </span>
                            <span className="text-xs text-gray-500">
                              💭 {refund.reason}
                            </span>
                            <div className={`text-xs px-2 py-1 rounded w-fit ${
                              refund.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {refund.status === 'paid' ? '💰 تم الدفع' : '❌ مرفوض'}
                            </div>
                          </div>
                          <div className="text-right space-y-1">
                            <div className="text-sm text-gray-600">
                              المبلغ الأصلي: {refund.original_amount?.toLocaleString()} دج
                            </div>
                            <div className="text-sm text-red-600">
                              رسوم: -{refund.fees_amount?.toLocaleString()} دج
                            </div>
                            <div className="text-lg font-bold text-green-600">
                              صافي المدفوع: {refund.net_refund_amount?.toLocaleString()} دج
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-sm text-gray-500">
                          <span>
                            {refund.status === 'paid' ? 'دفع بواسطة:' : 'رفض بواسطة:'} {refund.approved_by}
                          </span>
                          <span>
                            {new Date(refund.approved_at || refund.requested_at).toLocaleDateString('ar-DZ')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Refund Request Dialog */}
      {showRefundDialog && selectedOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">↩️ طلب استرجاع الأموال</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowRefundDialog(false);
                  setSelectedOperation(null);
                  setManualFees('');
                  setRefundReason('');
                  setRefundNotes('');
                  setCustomRefundAmount('');
                }}
                className="text-gray-500"
              >
                ✕
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Operation Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-bold mb-2">تفاصيل العملية الأصلية:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>رقم الوصل: <span className="font-medium">{selectedOperation.operation_no}</span></div>
                  <div>العميل: <span className="font-medium">{selectedOperation.client_name}</span></div>
                  <div>الخدمة: <span className="font-medium">{selectedOperation.service_name}</span></div>
                  <div>المبلغ الأصلي: <span className="font-medium text-green-600">{selectedOperation.final_price?.toLocaleString()} دج</span></div>
                  <div>التاريخ: <span className="font-medium">{new Date(selectedOperation.date).toLocaleDateString('ar-DZ')}</span></div>
                  <div>الأيام المنقضية: <span className="font-medium text-brand-600">
                    {Math.floor((new Date() - new Date(selectedOperation.date)) / (1000 * 60 * 60 * 24))} أيام
                  </span></div>
                </div>
              </div>

              {/* Refund Form */}
              <div className="space-y-4">
                <div>
                  <Label>نوع الاسترجاع</Label>
                  <select
                    value={refundType}
                    onChange={(e) => setRefundType(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="full_refund">↩️ استرجاع كامل</option>
                    <option value="partial_refund">⚡ استرجاع جزئي</option>
                    <option value="admin_refund">🏢 استرجاع إداري</option>
                  </select>
                </div>

                {refundType === 'partial_refund' && (
                  <div>
                    <Label>مبلغ الاسترجاع الجزئي (دج)</Label>
                    <Input
                      type="number"
                      value={customRefundAmount}
                      onChange={(e) => setCustomRefundAmount(e.target.value)}
                      placeholder="أدخل المبلغ"
                      max={selectedOperation.final_price}
                      min="0"
                      step="0.01"
                    />
                  </div>
                )}

                {/* NEW: Manual Fees Input */}
                <div>
                  <Label>رسوم الاسترجاع (دج) - إدخال يدوي</Label>
                  <Input
                    type="number"
                    value={manualFees}
                    onChange={(e) => {
                      const fee = parseFloat(e.target.value) || 0;
                      const maxFee = refundType === 'partial_refund' ? 
                        (parseFloat(customRefundAmount) || selectedOperation.final_price) : 
                        selectedOperation.final_price;
                      
                      if (fee <= maxFee) {
                        setManualFees(e.target.value);
                      }
                    }}
                    placeholder="أدخل مبلغ الرسوم (يمكن أن يكون 0)"
                    min="0"
                    max={refundType === 'partial_refund' ? 
                      (parseFloat(customRefundAmount) || selectedOperation.final_price) : 
                      selectedOperation.final_price}
                    step="0.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    الحد الأقصى: {(refundType === 'partial_refund' ? 
                      (parseFloat(customRefundAmount) || selectedOperation.final_price) : 
                      selectedOperation.final_price)?.toLocaleString()} دج
                  </p>
                </div>

                {/* Live Calculation Display */}
                <div className="bg-brand-50 p-4 rounded-lg">
                  <h4 className="font-bold mb-2">حساب الاسترجاع:</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>مبلغ الاسترجاع:</span>
                      <span className="font-medium">
                        {(refundType === 'partial_refund' ? 
                          (parseFloat(customRefundAmount) || selectedOperation.final_price) : 
                          selectedOperation.final_price)?.toLocaleString()} دج
                      </span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>رسوم الاسترجاع:</span>
                      <span className="font-medium">-{(parseFloat(manualFees) || 0).toLocaleString()} دج</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between text-lg font-bold text-green-600">
                      <span>صافي المبلغ المسترجع:</span>
                      <span>
                        {((refundType === 'partial_refund' ? 
                          (parseFloat(customRefundAmount) || selectedOperation.final_price) : 
                          selectedOperation.final_price) - (parseFloat(manualFees) || 0)).toLocaleString()} دج
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>سبب الاسترجاع *</Label>
                  <Input
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="سبب طلب الاسترجاع"
                    required
                  />
                </div>

                <div>
                  <Label>ملاحظات إضافية</Label>
                  <Input
                    value={refundNotes}
                    onChange={(e) => setRefundNotes(e.target.value)}
                    placeholder="ملاحظات (اختياري)"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={createRefund}
                    disabled={loading || !refundReason.trim()}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                  >
                    {loading ? 'جاري الإنشاء...' : '↩️ إنشاء طلب الاسترجاع'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRefundDialog(false);
                      setSelectedOperation(null);
                      setManualFees('');
                      setRefundReason('');
                      setRefundNotes('');
                      setCustomRefundAmount('');
                    }}
                    className="px-6"
                  >
                    إلغاء
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// NEW: Foreign Exchange Management Component  


export default RefundManagement;
